import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';

import FormBase from 'components/forms/new/FormBase';
import { Alert, AlertIcon, AlertDescription } from 'components/v4';
import useFieldValidation from 'hooks/useFieldValidation';
import { useAddOCProfile, useGetRegions } from './queries';
import { ocProfileSchema } from './schema';
import { formContainerStyles } from './constants';
import OperatorProfileField from './formFields/OperatorProfileField';
import OperatorConnectRegionsField from './formFields/OCRegionsField';
import { multiSelectDataMapper } from 'utils/utils';

const OCProfileAdd = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { darkMode } = useSelector((state) => state.settings);
    const { currentWholesaler } = useSelector((state) => state.navigationLists);
    const { mutate: addProfile, isLoading } = useAddOCProfile();
    const { data: regionsData } = useGetRegions(currentWholesaler);
    const [error, setError] = useState(null);
    const [regions, setRegions] = useState([]);

    const breadCrumbTrail = [
        { label: 'OC Profiles', path: '/operator-connect-profiles' },
        {
            label: 'Add OC Profile',
            path: `/operator-connect-profiles/add${location.search}`,
        },
    ];

    const { validatefn } = useFieldValidation({ regions }, ocProfileSchema);

    const handleSubmit = (values, actions) => {
        if (!validatefn()) {
            actions.setSubmitting(false);
            return;
        }

        setError(null);
        addProfile(
            {
                ...values,
                regions,
                wholesalerID: currentWholesaler,
            },
            {
                onSuccess: (data) => {
                    navigate(
                        `/operator-connect-profiles/${data.id}${location.search}`,
                    );
                },
                onError: (err) => {
                    setError(
                        err?.response?.data?.message || 'An error occurred.',
                    );
                    actions.setSubmitting(false);
                },
            },
        );
    };

    return (
        <Box
            sx={formContainerStyles.noHorizontalDivider}
            aria-label="Add OC Profile Page Form Container">
            <FormBase
                breadCrumbTrail={breadCrumbTrail}
                initialValues={{
                    name: '',
                    carrierName: '',
                    appID: '',
                    tenantID: '',
                    clientSecret: '',
                }}
                finalFormSubmit={handleSubmit}
                backLink={`/operator-connect-profiles${location.search}`}
                isLoading={isLoading}>
                {(formProps) => {
                    const { errors, touched, actions } = formProps;
                    return [
                        <OperatorProfileField
                            key="name"
                            fieldName="name"
                            label="Profile Name"
                            placeholder="Enter profile name"
                            errorMessage="Profile Name is required"
                            tooltipLabel="Enter the operator profile name."
                            tooltipAriaLabel="profile name tooltip"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <OperatorProfileField
                            key="carrierName"
                            fieldName="carrierName"
                            label="Carrier Name"
                            placeholder="Enter carrier name"
                            errorMessage="Carrier Name is required"
                            tooltipLabel="Enter the carrier name for this operator profile."
                            tooltipAriaLabel="carrier name tooltip"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <OperatorProfileField
                            key="appID"
                            fieldName="appID"
                            label="App ID"
                            placeholder="Enter App ID"
                            errorMessage="App ID is required"
                            tooltipLabel="Enter the Azure App ID for this operator profile."
                            tooltipAriaLabel="app id tooltip"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <OperatorProfileField
                            key="tenantID"
                            fieldName="tenantID"
                            label="Tenant ID"
                            placeholder="Enter Tenant ID"
                            errorMessage="Tenant ID is required"
                            tooltipLabel="Enter the Azure Tenant ID for this operator profile."
                            tooltipAriaLabel="tenant id tooltip"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <OperatorProfileField
                            key="clientSecret"
                            fieldName="clientSecret"
                            label="Client Secret"
                            placeholder="Enter Client Secret"
                            tooltipLabel="Enter the client secret for Azure authentication."
                            tooltipAriaLabel="client secret tooltip"
                            isRequired={false}
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <OperatorConnectRegionsField
                            key="regions"
                            darkMode={darkMode}
                            value={regions}
                            onChange={setRegions}
                            options={
                                regionsData?.map(multiSelectDataMapper) || []
                            }
                            error={
                                !regions || regions.length === 0
                                    ? 'At least one region is required'
                                    : ''
                            }
                            showError={actions?.isSubmitting}
                            disableStackHeight
                            isInvalid={false}
                        />,
                        error && (
                            <Box key="error-box" paddingBottom="0.5rem">
                                <Alert
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ];
                }}
            </FormBase>
        </Box>
    );
};

export default OCProfileAdd;
